import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import moment from 'moment';
import { toast } from 'react-toastify';

import { clientFamilyBackgroundActions } from '../../../../../store';
import { SupervisionNoteStatus } from '../../../../../constants';
import { TIME_RANGE_FIELDS } from '../../../../../constants';
import { DEFAULT_PARENT_SUPERVISION } from '../constants/general';

import type { IParentSupervision, ISelectOption } from '../../../../../utils/types';
import type { ParentSupervisionUpdateHandler } from '../../types';

type UpdateFieldFn = <K extends keyof IParentSupervision>(
  key: K,
  value: IParentSupervision[K],
  triggerApiCall?: boolean,
) => void;

type UpdateFieldObjectFn = (updates: Partial<IParentSupervision>, triggerApiCall?: boolean) => void;

interface UseParentSupervisionFormParams {
  parentSupervision: IParentSupervision | null;
  settings: ISelectOption[];
  onUpdate: ParentSupervisionUpdateHandler;
}

function useParentSupervisionForm({
  parentSupervision,
  settings,
  onUpdate,
}: UseParentSupervisionFormParams) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const [values, setValues] = useState<IParentSupervision>(DEFAULT_PARENT_SUPERVISION);
  const fetchedTreatmentPlanIdRef = useRef<number | null>(null);

  const hasPersistedSupervision = Boolean(parentSupervision?.id);

  useEffect(() => {
    if (!parentSupervision) {
      setValues(DEFAULT_PARENT_SUPERVISION);
      return;
    }

    const nextValues: IParentSupervision = {
      ...parentSupervision,
      setting:
        !parentSupervision.setting && settings.length === 1
          ? settings[0].value.toString()
          : parentSupervision.setting,
    };
    setValues(nextValues);
  }, [parentSupervision, settings]);

  useEffect(() => {
    if (!hasPersistedSupervision || !parentSupervision) {
      fetchedTreatmentPlanIdRef.current = null;
      return;
    }

    if (!parentSupervision.active_treatment_plan_id) {
      toast.error('Client does not have an active treatment plan');
      return;
    }

    if (parentSupervision.status !== SupervisionNoteStatus.Started) return;

    const treatmentPlanId = parentSupervision.active_treatment_plan_id;
    if (fetchedTreatmentPlanIdRef.current === treatmentPlanId) return;
    fetchedTreatmentPlanIdRef.current = treatmentPlanId;

    dispatch(
      clientFamilyBackgroundActions.getClientFamilyBackground({
        treatmentPlanId,
        getAccessTokenSilently,
      }),
    );
  }, [
    dispatch,
    getAccessTokenSilently,
    hasPersistedSupervision,
    parentSupervision,
    parentSupervision?.active_treatment_plan_id,
    parentSupervision?.status,
  ]);

  const handleUpdateField: UpdateFieldFn = useCallback(
    (key, value, triggerApiCall = true) => {
      if (parentSupervision && parentSupervision[key] === value) return;

      const nextValues: IParentSupervision = { ...values, [key]: value };

      if (key === TIME_RANGE_FIELDS.DATE_START && moment.isMoment(nextValues.date_start)) {
        nextValues.date_start = nextValues.date_start.isAfter(moment())
          ? moment()
          : nextValues.date_start;
      }

      setValues(nextValues);
      onUpdate(nextValues, undefined, triggerApiCall);
    },
    [onUpdate, parentSupervision, values],
  );

  const handleUpdateFieldObject: UpdateFieldObjectFn = useCallback(
    (updates, triggerApiCall = true) => {
      const nextValues: IParentSupervision = { ...values, ...updates };
      setValues(nextValues);
      onUpdate(nextValues, undefined, triggerApiCall);
    },
    [onUpdate, values],
  );

  return {
    values,
    hasPersistedSupervision,
    setValues,
    handleUpdateField,
    handleUpdateFieldObject,
  };
}

export default useParentSupervisionForm;
